import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import debounce from 'lodash/debounce';
import { useRoute, useRouter } from '@/composables/common/router/useRouter';

/**
 * useQueryList — Factory que elimina el boilerplate de listados paginados
 * con filtros sincronizados con la URL.
 *
 * Elimina la repetición de: filters ref, meta computed, updateUrlQuery,
 * debouncedFetch, watch filters, hydratación desde URL, y cleanup.
 *
 * @param {Object} options
 * @param {Function} options.fetcher - async (params) => data
 *     Recibe los params ya armados, debe devolver { data: [], meta: {} }
 * @param {string[]} [options.extraFilters=[]] - Filtros extra (ej: ['talla', 'categoria'])
 * @param {Object} [options.defaultFilterValues={}] - Valores por defecto para filtros extra
 * @param {number} [options.debounceMs=500] - Delay del debounce
 * @param {boolean} [options.urlSync=true] - Sincronizar filters con URL query
 * @param {string[]} [options.preserveQueryKeys=[]] - Otras claves de query que deben
 *     conservarse al sincronizar los filtros
 * @param {boolean} [options.resetFiltersOnMount=false] - Ignorar búsqueda,
 *     página y filtros extra de la URL durante la carga inicial
 * @param {boolean} [options.clampPageToLastPage=false] - Reintentar en la última
 *     página válida si la página solicitada ya no existe
 * @param {boolean} [options.flushUrlOnUnmount=true] - Sincronizar filtros con
 *     la URL al desmontar
 * @param {boolean} [options.autoFetch=true] - Fetch automático al montar
 * @param {boolean} [options.watchListaId=false] - Hacer fetch si cambia route.params.lista_id
 * @param {'route'|'store'|'none'} [options.listaIdSource='route'] - De dónde sacar lista_id
 * @param {Function} [options.getListaId] - Función que devuelve el listaId.
 *     Requerido si listaIdSource === 'store'. Ej: () => store.getters.getListaId
 */
export function useQueryList({
    fetcher,
    extraFilters = [],
    defaultFilterValues = {},
    debounceMs = 500,
    urlSync = true,
    preserveQueryKeys = [],
    resetFiltersOnMount = false,
    clampPageToLastPage = false,
    flushUrlOnUnmount = true,
    autoFetch = true,
    watchListaId = false,
    listaIdSource = 'route',
    getListaId = null,
} = {}) {
    if (!fetcher) {
        throw new Error('[useQueryList] fetcher function is required');
    }

    const route = listaIdSource === 'route' ? useRoute() : null;
    const router = urlSync ? useRouter() : null;
    const getCurrentRoute = () => router?.currentRoute || route;

    // ── State ───────────────────────────────────────────────────────────────

    const data = ref({ data: [], meta: {} });
    const loading = ref(false);

    const defaultFilters = {
        search: '',
        page: 1,
        per_page: 10,
        ...Object.fromEntries(extraFilters.map(f => [f, defaultFilterValues[f] ?? ''])),
    };

    const filters = ref({ ...defaultFilters });
    let pageCorrection = null;
    let suppressUrlSyncForNextFilterChange = false;
    let prevFilterSnapshot = { ...defaultFilters };

    const meta = computed(() => data.value.meta || {});

    const listaId = computed(() => {
        if (listaIdSource === 'route' && getCurrentRoute()) {
            return getCurrentRoute().params.lista_id;
        }
        if (listaIdSource === 'store') {
            if (!getListaId) {
                throw new Error(
                    '[useQueryList] listaIdSource "store" requiere la opción getListaId'
                );
            }
            return getListaId();
        }
        return null;
    });

    // ── Fetch ───────────────────────────────────────────────────────────────

    async function fetch(lista_id, { silent = false } = {}) {
        const f = filters.value;
        const requestedFilters = {
            search: f.search,
            page: f.page,
            per_page: f.per_page,
            ...extraFilters.reduce((acc, key) => {
                if (f[key] !== undefined && f[key] !== '') acc[key] = f[key];
                return acc;
            }, {}),
        };
        const params = {
            filters: requestedFilters,
            lista_id: lista_id ?? listaId.value,
        };

        if (!silent) loading.value = true;
        try {
            let result = await fetcher(params);

            const lastPage = Number(result?.meta?.last_page);
            const filtrosSiguenIguales = Object.keys(requestedFilters).every(
                key => filters.value[key] === requestedFilters[key]
            );

            if (
                clampPageToLastPage &&
                Number.isFinite(lastPage) &&
                lastPage >= 1 &&
                requestedFilters.page > lastPage &&
                filtrosSiguenIguales
            ) {
                const correctedFilters = { ...requestedFilters, page: lastPage };
                pageCorrection = { ...filters.value, page: lastPage };
                filters.value.page = lastPage;
                prevFilterSnapshot = { ...filters.value };
                updateUrlQuery();

                result = await fetcher({
                    filters: correctedFilters,
                    lista_id: params.lista_id,
                });
            }

            data.value = result || { data: [], meta: {} };
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useQueryList] Fetch error:', error);
            }
            data.value = { data: [], meta: {} };
        } finally {
            if (!silent) loading.value = false;
        }
    }

    // ── URL Sync ────────────────────────────────────────────────────────────

    const isHydrating = ref(false);

    function updateUrlQuery() {
        if (!urlSync || !router) return;
        const f = filters.value;
        const query = {};

        const currentRoute = getCurrentRoute();
        if (currentRoute) {
            preserveQueryKeys.forEach(key => {
                if (currentRoute.query[key] !== undefined) query[key] = currentRoute.query[key];
            });
        }

        if (f.search) query.search = f.search;
        if (f.page && f.page > 1) query.page = String(f.page);
        if (f.per_page && f.per_page !== 10) query.per_page = String(f.per_page);
        extraFilters.forEach(key => {
            if (f[key]) query[key] = f[key];
        });

        router.replace({ query }).catch(() => {});
    }

    const debouncedFetch = debounce(id => fetch(id), debounceMs);
    const updateUrlDebounced = urlSync
        ? debounce(() => updateUrlQuery(), debounceMs)
        : null;

    // ── URL sync strategy ───────────────────────────────────────────────────
    // We deliberately DO NOT sync `?search=` to the URL on every keystroke.
    // router.replace on each character triggers a route change that can cause
    // the search input to lose focus mid-typing (the parent view re-renders,
    // the SearchComponent subtree gets repainted and `document.activeElement`
    // falls back to <body>).
    //
    // The watcher below still fetches immediately, but only schedules a URL
    // sync when something OTHER than `search` changed (page, per_page, or any
    // extraFilters). The latest `search` is flushed synchronously on unmount
    // when `flushUrlOnUnmount` is enabled. Event lists keep the URL current
    // while mounted and disable this flush because their route may be changing.

    const nonSearchKeys = () => ['page', 'per_page', ...extraFilters];

    // ── Watchers ────────────────────────────────────────────────────────────

    if (urlSync) {
        watch(filters, newVal => {
            if (isHydrating.value) return;

            if (pageCorrection) {
                const correctionMatches = Object.keys(pageCorrection).every(
                    key => newVal[key] === pageCorrection[key]
                );
                pageCorrection = null;
                if (correctionMatches) {
                    prevFilterSnapshot = { ...newVal };
                    return;
                }
            }

            // Si cambia el término de búsqueda, reseteamos la página a 1.
            // Si estamos en page>1 con un search que no estaba, el backend
            // devuelve vacío (el set filtrado no tiene tantas páginas).
            const searchChanged = newVal.search !== prevFilterSnapshot.search;
            const pageResetNeeded = searchChanged && newVal.page !== 1;
            if (pageResetNeeded) {
                filters.value.page = 1;
            }

            debouncedFetch();

            // effectiveNew refleja el estado actual de filters (post-reset)
            // para que la comparación de otherChanged y el snapshot sean correctos.
            const effectiveNew = { ...newVal, page: filters.value.page };
            const otherChanged = nonSearchKeys().some(
                key => effectiveNew[key] !== prevFilterSnapshot[key]
            );
            if (otherChanged) {
                if (suppressUrlSyncForNextFilterChange) {
                    suppressUrlSyncForNextFilterChange = false;
                } else {
                    updateUrlDebounced();
                }
            }

            prevFilterSnapshot = effectiveNew;
        }, { deep: true });
    }

    if (watchListaId && route) {
        watch(() => getCurrentRoute().params.lista_id, (newId, oldId) => {
            if (newId && newId !== oldId) fetch();
        });
    }

    // ── Lifecycle ───────────────────────────────────────────────────────────

    if (autoFetch || urlSync) {
        onMounted(() => {
            if (urlSync && route) {
                const query = getCurrentRoute().query;
                const filterQueryKeys = ['search', 'page', 'per_page', ...extraFilters];
                const hasFilterQuery = filterQueryKeys.some(key => query[key] !== undefined);
                if (hasFilterQuery) {
                    isHydrating.value = true;
                    if (query.search && !resetFiltersOnMount) filters.value.search = query.search;
                    if (query.page && !resetFiltersOnMount) {
                        filters.value.page = parseInt(query.page, 10);
                    }
                    if (query.per_page) filters.value.per_page = parseInt(query.per_page, 10);
                    extraFilters.forEach(key => {
                        if (query[key] && !resetFiltersOnMount) filters.value[key] = query[key];
                    });
                    nextTick(() => {
                        isHydrating.value = false;
                        // El watcher compara contra este snapshot: si no lo
                        // sincronizamos con el estado ya hidratado, el primer
                        // cambio del usuario tras un `?search=`/`?per_page=`
                        // se lee como "cambió la búsqueda" y fuerza page = 1
                        // (el pie de tabla pierde la página y no sale petición).
                        prevFilterSnapshot = { ...filters.value };
                    });
                }
            }

            if (autoFetch) fetch();
        });
    }

    onUnmounted(() => {
        debouncedFetch.cancel();
        if (updateUrlDebounced) {
            updateUrlDebounced.cancel();
            if (flushUrlOnUnmount) {
                // Final flush: persist the latest `search` to the URL so navigating
                // away and back (or sharing the URL) keeps the query. This is a
                // single synchronous router.replace right before unmount, so it
                // does not cause the input-focus issue described above.
                updateUrlQuery();
            }
        }
    });

    // ── Reset ───────────────────────────────────────────────────────────────

    function resetFilters() {
        filters.value = { ...defaultFilters };
    }

    function suppressNextUrlSync() {
        suppressUrlSyncForNextFilterChange = true;
    }

    // ── Return ──────────────────────────────────────────────────────────────

    return {
        data,
        loading,
        filters,
        meta,
        listaId,
        fetch,
        debouncedFetch,
        updateUrlQuery,
        updateUrlDebounced,
        isHydrating,
        resetFilters,
        suppressNextUrlSync,
    };
}
